import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus, Check, X } from 'lucide-react';

/**
 * Simple single-select combobox with inline creation.
 * props:
 *  - value: current selected value (string)
 *  - options: string[]  (current list of choices)
 *  - onChange: (newValue, updatedOptions) => void
 *    Receives both the new value and the full options list (so the parent can persist new entries)
 *  - placeholder?: string
 *  - testId?: string
 *  - allowClear?: boolean  (show 'x' to clear)
 */
export default function ComboboxCreate({ value, options, onChange, placeholder = 'Sélectionner ou créer…', testId, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [internalOptions, setInternalOptions] = useState(options || []);
  const ref = useRef(null);

  useEffect(() => { setInternalOptions(options || []); }, [options]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return internalOptions;
    return internalOptions.filter((o) => o.toLowerCase().includes(q));
  }, [internalOptions, query]);

  const normalizedQuery = query.trim();
  const showCreate = normalizedQuery.length > 0 && !internalOptions.some((o) => o.toLowerCase() === normalizedQuery.toLowerCase());

  const select = (v) => {
    setOpen(false);
    setQuery('');
    onChange(v, internalOptions);
  };

  const create = () => {
    const v = normalizedQuery;
    if (!v) return;
    const next = [...internalOptions, v];
    setInternalOptions(next);
    setOpen(false);
    setQuery('');
    onChange(v, next);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('', internalOptions);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="w-full h-10 px-3 pr-9 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] text-left focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors flex items-center justify-between"
      >
        <span className={value ? 'text-[#1A1B2E]' : 'text-[#8E8FA5]'}>
          {value || placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {allowClear && value && (
            <span
              onClick={clear}
              className="p-0.5 rounded hover:bg-[#F3F4F8] text-[#8E8FA5]"
              title="Effacer"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-[#8E8FA5] transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white rounded-xl border border-[#EFF0F6] shadow-lg p-1" style={{ boxShadow: '0 6px 24px rgba(26,27,46,0.12)' }}>
          <div className="px-2 pt-1 pb-2 border-b border-[#F3F4F8]">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher ou créer…"
              className="w-full h-8 px-2 rounded-lg bg-[#F6F7FB] text-[12.5px] outline-none focus:bg-white"
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto">
            {filtered.map((o) => (
              <button
                key={o}
                type="button"
                data-testid={`${testId}-opt-${o.replace(/\s+/g, '-')}`}
                onClick={() => select(o)}
                className={`w-full text-left text-[13px] px-3 py-1.5 rounded-lg flex items-center justify-between hover:bg-[#EEF2FF] ${value === o ? 'text-[#4F46E5] font-semibold' : 'text-[#4A4B63]'}`}
              >
                <span className="truncate">{o}</span>
                {value === o && <Check className="w-3.5 h-3.5 shrink-0" />}
              </button>
            ))}
            {filtered.length === 0 && !showCreate && (
              <div className="text-[12px] italic text-[#8E8FA5] text-center py-3">Aucun résultat</div>
            )}
            {showCreate && (
              <button
                type="button"
                data-testid={`${testId}-create-btn`}
                onClick={create}
                className="w-full text-left text-[13px] px-3 py-1.5 rounded-lg flex items-center gap-2 hover:bg-[#EEF2FF] text-[#4F46E5] font-semibold border-t border-[#F3F4F8] mt-1"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="truncate">Créer « {normalizedQuery} »</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
